/**
 * OsCountModeReady — MADE-292
 *
 * Route: /os/count-mode/ready  (public — no PIN required while in Count Mode)
 *
 * The idle "scan next item" screen shown between item counts. Count Mode is
 * visually distinct from normal OS pages: amber/orange colour scheme instead
 * of navy, making it immediately obvious that the device is in counting mode.
 *
 * Navigation state (from OsScanItem after a successful count):
 *   { lastItemName?: string }
 * The current countedItems is read from localStorage (already updated before navigate).
 *
 * If no count mode state is found in localStorage, redirects to /os/scan.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Divider, LinearProgress, List, ListItemButton, ListItemText, Snackbar, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useCountMode } from '../hooks/useCountMode';
import { scanDestination } from '../utils/scanDestination';
import { useScanListener } from '../hooks/useScanListener';
import { OsCountModeBanner } from './OsCountModeBanner';
import { TenantProvider } from '../../contexts/TenantContext';

interface LocationState {
  lastItemName?: string;
  successMsg?: string;    // #3: count success message to display briefly
  offRosterWarning?: string; // #5: item not on count list warning
}

const OsCountModeReadyInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCountMode } = useCountMode();

  const navState = location.state as LocationState | null;
  const lastItemName     = navState?.lastItemName;
  const successMsg       = navState?.successMsg;
  const offRosterWarning = navState?.offRosterWarning;
  const [snackMsg, setSnackMsg] = React.useState<string | null>(offRosterWarning ?? successMsg ?? null);
  const snackSeverity = offRosterWarning ? 'warning' : 'success';
  const [viewMode, setViewMode] = useState<'scan' | 'list'>('scan');
  const [sessionItems, setSessionItems] = useState<Array<{
    itemNumber: number; description: string; location: string; countedQty: number | null; status: string;
  }> | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);

  const countMode = getCountMode();

  // Guard: if no active count mode, fall back to scan entry
  useEffect(() => {
    if (!countMode) {
      navigate('/os/scan', { replace: true });
    }
  }, [countMode, navigate]);

  // Scan listener: item QR → navigate to count mode item page
  const handleScan = useCallback(
    (value: string) => {
      const destination = scanDestination(value);
      if (destination) navigate(destination);
    },
    [navigate],
  );

  // Count session QR scanned while on ready screen — re-enter that session
  const handleCountSession = useCallback(
    (sessionId: string) => {
      navigate(`/os/count/${encodeURIComponent(sessionId)}`);
    },
    [navigate],
  );

  // A progress refresh may include newly added roster items or another device's count.
  useEffect(() => {
    setSessionItems(null);
    setItemsError(null);
  }, [countMode?.sessionId, countMode?.totalItems, countMode?.countedItems]);

  // #2: Fetch session items when list view is opened
  const fetchItems = useCallback(async () => {
    if (!countMode?.sessionId) return;
    setItemsLoading(true);
    setItemsError(null);
    try {
      const res = await fetch(`/api/os/count-sessions/${encodeURIComponent(countMode.sessionId)}/items`);
      if (!res.ok) throw new Error(`Unable to load items (HTTP ${res.status}).`);
      const data = await res.json();
      setSessionItems((data.items ?? []).map((item: {
        itemNumber: number; materialName: string; location: string; actualCount: number | null;
      }) => ({
        itemNumber: item.itemNumber,
        description: item.materialName,
        location: item.location,
        countedQty: item.actualCount,
        status: item.actualCount == null ? 'pending' : 'counted',
      })));
    } catch (error) {
      setItemsError(error instanceof Error ? error.message : 'Unable to load items.');
    }
    finally { setItemsLoading(false); }
  }, [countMode?.sessionId]);

  useEffect(() => {
    if (viewMode === 'list' && sessionItems === null && !itemsError) {
      fetchItems();
    }
  }, [viewMode, sessionItems, fetchItems, itemsError]);

  useScanListener(handleScan, handleCountSession);

  if (!countMode) return null;

  const { countedItems, totalItems } = countMode;
  const complete = countMode.rosterMode !== 'open' && totalItems > 0 && countedItems >= totalItems;
  const pct = totalItems > 0 ? Math.round((countedItems / totalItems) * 100) : 0;

  return (
    <>
    <Box
      sx={{ minHeight: '100dvh', bgcolor: '#FFF8E1', display: 'flex', flexDirection: 'column' }}
      data-testid="count-mode-ready"
    >
      <OsCountModeBanner />

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          gap: 3,
        }}
      >
        {/* Last counted confirmation — only shown after a count */}
        {lastItemName && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: '#2e7d32',
              bgcolor: '#e8f5e9',
              px: 2.5,
              py: 1,
              borderRadius: 2,
            }}
            data-testid="last-counted"
          >
            <CheckCircleIcon fontSize="small" />
            <Typography variant="body2" fontWeight={600} data-testid="last-counted-name">
              Last counted: {lastItemName}
            </Typography>
          </Box>
        )}

        {/* #8: Audit complete banner */}
        {complete && (
          <Box sx={{ bgcolor: '#e8f5e9', border: '2px solid #2e7d32', borderRadius: 3, p: 3, textAlign: 'center', maxWidth: 360, width: '100%' }} data-testid="audit-complete-banner">
            <Typography variant="h6" fontWeight={700} color="#2e7d32">✓ All items counted!</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Return this device to your manager to confirm changes and close the session.</Typography>
          </Box>
        )}

        {/* #2: View toggle */}
        {totalItems > 0 && !complete && (
          <Button size="small" variant="outlined" color="inherit" onClick={() => setViewMode(v => v === 'scan' ? 'list' : 'scan')} sx={{ alignSelf: 'flex-end', mr: 0.5 }} data-testid="toggle-view-btn">
            {viewMode === 'scan' ? 'View Item List' : 'Back to Scanner'}
          </Button>
        )}

        {/* Scan prompt card — shown in scan mode when not complete */}
        {viewMode === 'scan' && !complete && (
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            boxShadow: 2,
            maxWidth: 360,
            width: '100%',
            border: '2px solid #F57C00',
          }}
        >
          <QrCodeScannerIcon sx={{ fontSize: 64, color: '#F57C00', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} color="#E65100">
            Scan next item to count
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Point the scanner at an item QR code
          </Typography>
        </Box>
        )}

        {/* #2: Item list mode */}
        {viewMode === 'list' && (
          <Box sx={{ width: '100%', maxWidth: 480, bgcolor: '#fff', borderRadius: 3, boxShadow: 2, overflow: 'hidden' }}>
            {itemsLoading
              ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
              : itemsError ? <Alert severity="error" action={<Button onClick={fetchItems}>Retry</Button>}>{itemsError}</Alert> : (
                <List dense disablePadding>
                  {(sessionItems ?? []).map((item, i) => (
                    <React.Fragment key={item.itemNumber}>
                      {i > 0 && <Divider />}
                      <ListItemButton
                        onClick={() => navigate(`/os/scan/${item.itemNumber}/${item.location}`, { state: { countMode: countMode?.sessionId } })}
                        data-testid={`item-row-${item.itemNumber}`}
                      >
                        <ListItemText
                          primary={<><strong>{String(item.itemNumber).padStart(6,'0')}</strong> — {item.description}</>}
                          secondary={item.location}
                        />
                        <Chip
                          label={item.countedQty != null ? `${item.countedQty} ✓` : 'pending'}
                          size="small"
                          color={item.countedQty != null ? 'success' : 'default'}
                          sx={{ ml: 1 }}
                        />
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                  {(sessionItems ?? []).length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">No items in this session.</Typography>
                    </Box>
                  )}
                </List>
              )
            }
          </Box>
        )}

        {/* Progress */}
        <Box sx={{ width: '100%', maxWidth: 360 }}>
          <Typography
            variant="body2"
            sx={{ color: '#E65100', fontWeight: 600, mb: 1, textAlign: 'center' }}
            data-testid="progress-text"
          >
            {countedItems} of {totalItems} items counted
          </Typography>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 10,
              borderRadius: 5,
              bgcolor: 'rgba(245, 124, 0, 0.2)',
              '& .MuiLinearProgress-bar': { bgcolor: '#F57C00', borderRadius: 5 },
            }}
            aria-label={`Count progress: ${pct}%`}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
            data-testid="progress-pct"
          >
            {pct}%
          </Typography>
        </Box>
      </Box>
    </Box>
    <Snackbar
      open={!!snackMsg}
      autoHideDuration={snackSeverity === 'warning' ? 5000 : 2000}
      onClose={() => setSnackMsg(null)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackMsg(null)} severity={snackSeverity} sx={{ width: '100%' }}>
        {snackMsg}
      </Alert>
    </Snackbar>
    </>
  );
};

/**
 * Wrap in TenantProvider since this is a public route (no OsSessionGuard /
 * no outer TenantProvider from the route tree).
 */
export const OsCountModeReady: React.FC = () => (
  <TenantProvider tenant="offshore">
    <OsCountModeReadyInner />
  </TenantProvider>
);
